import polyglotI18nProvider from 'ra-i18n-polyglot';
import {
  Admin,
  CustomRoutes,
  Resource,
  localStorageStore,
  useStore,
  StoreContextProvider,
  //addRefreshAuthToDataProvider,
} from 'react-admin';
import { BrowserRouter, Route } from 'react-router-dom';

import { LoginPage } from "ra-auth-msal";
import { CustomLoginPage } from './auth/CustomLoginPage';
import { authProvider, redirectOnCheckAuth } from './auth/authProvider';

import englishMessages from './i18n/en';
import { AppLayout } from './layout';
import schools from './pages/admin/schools';
import { themes, ThemeName } from './themes/themes';
import { ZipMapPage } from './pages/zipMap';
import { SchoolsMapPage } from './pages/schoolsMap';
import { TransactionsMapPage } from './pages/transactionsMap';
import { NeighborhoodMapPage } from './pages/neighborhoodMap';
import { AboutPage } from './pages/about/AboutPage';
import { LandingPage } from './pages/landing/LandingPage';
import { SettingsPage } from './pages/settings';
import { estateDataProvider } from './dataProvider';
import { PricingPage } from './pages/pricing/PricingPage';
import { AudiencePage } from './pages/audience/AudiencePage';
import { HowTosPage } from './pages/howtos/HowTosPage';
import { RequireAccess } from './payWall/RequireAccess';

import ReactGA from "react-ga4";
import { GaAnalyticsTracker } from './instruments/gaAnalyticsTracker';
import Clarity from '@microsoft/clarity';

ReactGA.initialize(import.meta.env.VITE_APP_GA_TAG);
Clarity.init(import.meta.env.VITE_APP_CLARITY_PROJECT_ID);


const i18nProvider = polyglotI18nProvider(
  locale => {
    if (locale === 'es') {
      return import('./i18n/es').then(messages => messages.default);
    }

    // Always fallback on english
    return englishMessages;
  },
  'en',
  [
    { locale: 'en', name: 'English' },
    { locale: 'sp', name: 'Spanish' },
  ]
);

const store = localStorageStore(undefined, 'MyRealStat');


const App = () => {
  const [themeName] = useStore<ThemeName>('themeName', 'myrealstat');
  const lightTheme = themes.find(theme => theme.name === themeName)?.light;
  const darkTheme = themes.find(theme => theme.name === themeName)?.dark;

  return (
    <BrowserRouter>
      <Admin
        title={import.meta.env.VITE_APP_TITLE}
        dataProvider={estateDataProvider}
        store={store}
        authProvider={authProvider}
        requireAuth={false}
        loginPage={redirectOnCheckAuth ? LoginPage : CustomLoginPage}
        layout={AppLayout}
        i18nProvider={i18nProvider}
        disableTelemetry
        lightTheme={darkTheme}
        darkTheme={darkTheme}
      >
        <GaAnalyticsTracker />
        <CustomRoutes>
          <Route path="/" element={<LandingPage />} />
        </CustomRoutes>
        {/*<Resource name="schools" {...schools} /> */}
        <Resource name="data/zip" list={<ZipMapPage />} />
        <Resource name="data/neighborhood" list={
          <RequireAccess membership='Novice'>
            <NeighborhoodMapPage />
          </RequireAccess>
        } />
        <Resource name="data/schools" list={
          <RequireAccess membership='Pro'>
            <SchoolsMapPage />
          </RequireAccess>
        } />
        <CustomRoutes>
          <Route path="/transactions-map" element={
            <RequireAccess membership='Pro'>
              <TransactionsMapPage />
            </RequireAccess>
          } />
        </CustomRoutes>
        <CustomRoutes>
          <Route path="pricing" element={<PricingPage />} />
        </CustomRoutes>
        <CustomRoutes>
          <Route path="howto" element={<HowTosPage />} />
        </CustomRoutes>
        <CustomRoutes>
          <Route path="audience" element={<AudiencePage />} />
        </CustomRoutes>
        <CustomRoutes>
          <Route path="about" element={<AboutPage />} />
        </CustomRoutes>
        <CustomRoutes>
          <Route path="settings" element={<SettingsPage />} />
        </CustomRoutes>
      </Admin>
    </BrowserRouter>
  );
};

const AppWrapper = () => (
  <StoreContextProvider value={store}>
    <App />
  </StoreContextProvider>
);

export default AppWrapper;
